// PHASE_1 / B0 — the ADR-014 §2 Shell/Content worked example, now on keyset.
// Faz 8.6 / Batch A2 restyles the VISUAL layer to the novem people language
// (NovemHR-ekranlar personnel.jsx + emp-directory.jsx + emp-profile.jsx port):
// serif PageHeader with a count lead, the KPI StatCard strip, segmented
// lifecycle tabs with count pills, the novem table language (Sicil mono cell,
// Personel avatar cell), the directory grid/list card views with a sticky
// person panel, and the self-profile hero. The DATA layer is untouched — the
// SAME fetchEmployeesPage keyset contract; the only additions are two
// screen-local fetchers following the dashboard golden-sample pattern
// (GET /v1/me to resolve the caller's own employee id; GET /v1/employees/{id}
// for the profile record). One person's record is a PAGE of its own
// (employee-detail-content.tsx, SEM-6354), not a drawer over this list.
//
// EmployeeListContent stays a PURE, shell-agnostic Content component: the
// SAME component renders under the SINGLE parameterized AppShell in all three
// routes — /people (admin hat), /me/directory and /me/profile (employee hat)
// — via router.tsx. One implementation; the surface variant is resolved from
// the route path INSIDE the component (visual-layer branching only — the
// no-copy rule stays structural, ADR-014 §2). The server scopes the personnel
// rows per caller (employee_self callers get only their own row).
//
// Mockup elements with no backend are SKIPPED, not faked (port rule 8):
// leave-balance/pending COLUMNS (no leave join on employee rows — the own-
// profile balance card reads GET /v1/leave/balances?mine=true instead),
// extension/location fields (bare UUIDs only), the export action and messaging
// buttons. "Personel ekle" is wired (/people/create) and the personnel-detail
// tabs live on the person's own page (employee-detail-tabs.tsx).
// /me/directory is the exception: it reads its own endpoint
// (GET /v1/employees/directory), because the personnel list a plain employee is
// scoped to holds one row, their own (E27). The
// mockup's "unvansız görünüm" stance is therefore retired: the directory shows
// name, work email, job title and department, and nothing from the personnel
// record (see DirectoryView).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router";
import {
  Building2,
  CalendarCheck2,
  CalendarDays,
  CircleCheck,
  FileText,
  LayoutGrid,
  Mail,
  Network,
  Package,
  ReceiptText,
  Rows3,
  Search,
  UserPlus,
  Users,
} from "lucide-react";

import { type EmployeeLifecycleState } from "@novemai/hris-sdk";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Empty,
  ErrorState,
  FieldRow,
  Input,
  LanguageSwitcher,
  Loading,
  MeterBar,
  PageHeader,
  PersonCard,
  Select,
  StatCard,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  ViewToggle,
  type DataTableColumn,
} from "@novemai/components";
import { DepartmentLink } from "@/components/department-link";
import { PersonLink } from "@/components/person-link";

import { platformGraph } from "@/api/platform-graph";
import {
  errorMessage,
  unwrapEnvelope,
  type EnvelopedFetchResult,
} from "@/api/errors";
import { useKeysetQuery, type KeysetPage } from "@/api/keyset";
import { useTimeZonePreference } from "@/api/use-time-zone";
import { fetchEmployeeSummary, type EmployeeRow } from "@/features/employees/api";
import { PersonalInfoCard } from "@/features/employees/personal-info-card";
import {
  DIRECTORY_KEY,
  fetchDirectoryPage,
  type DirectoryEntry,
} from "@/features/employees/directory-api";
import {
  fullName,
  initialsOf,
  LIFECYCLE_KEY,
  LIFECYCLE_TONE,
} from "@/features/employees/employee-display";
import { fetchLeaveBalancesPage } from "@/features/leave/leave-api";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { useLocalePreference } from "@/i18n/useLocalePreference";
import { formatDate, formatTenureDuration } from "@/lib/date-range";
import { formatLeaveSpan } from "@/features/leave/leave-days";
import { formatCountWithMore } from "@/lib/count";
import { supportedTimeZones, timeZoneLabel } from "@/lib/time-zone-options";
import { useTabParam } from "@/lib/use-tab-param";
import { useDebouncedSearchParam, useFilterParam } from "@/lib/use-url-filters";

// ---- data (the B0 keyset contract, widened to the full row the route
// already returns — services/platform-graph/src/routes/employees.ts). The row
// shape is the single EmployeeRow exported by the employees feature api (SSOT;
// it carries the SEM-301 manager_name/department_name display-name joins). ----

// These signatures include the query parameters and envelopes consumed here.
// The generated paths carry uneven request metadata, so the adapter makes
// this list screen's wire contract explicit.
type EmployeesGet = (
  path: "/v1/employees",
  init: {
    params: {
      query: { cursor?: string; lifecycle_state?: string; department_id?: string; q?: string };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<EmployeeRow>>>;

interface EmployeesPageFilter {
  /** Virgüllü liste: `active,on_leave` iki durumdan birini okur. */
  readonly lifecycleState?: string;
  readonly departmentId?: string;
  readonly q?: string;
}

// EVERY narrowing is server-side (the backend GET /v1/employees accepts
// `lifecycle_state` — a comma list — plus `department_id` and `q`). Filtering
// the loaded keyset window in the browser silently hid every row past page one
// (E10: a search for a person on page two found nobody), so the window is never
// the thing that narrows.
async function fetchEmployeesPage(
  cursor: string | null,
  filter: EmployeesPageFilter,
): Promise<KeysetPage<EmployeeRow>> {
  const get = platformGraph.GET as unknown as EmployeesGet;
  const result = get("/v1/employees", {
    params: {
      query: {
        ...(cursor !== null && { cursor }),
        ...(filter.lifecycleState !== undefined && { lifecycle_state: filter.lifecycleState }),
        ...(filter.departmentId !== undefined && { department_id: filter.departmentId }),
        ...(filter.q !== undefined && filter.q !== "" && { q: filter.q }),
      },
    },
  });
  return unwrapEnvelope(result);
}

type EmployeeGet = (
  path: "/v1/employees/{id}",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ employee: EmployeeRow }>>;

async function fetchEmployee(id: string): Promise<EmployeeRow> {
  const get = platformGraph.GET as unknown as EmployeeGet;
  const result = get("/v1/employees/{id}", { params: { path: { id } } });
  const data = await unwrapEnvelope(result);
  return data.employee;
}

interface MeIdentity {
  readonly employee_id: string | null;
}

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeIdentity>>;

async function fetchMe(): Promise<MeIdentity> {
  const get = platformGraph.GET as unknown as MeGet;
  return unwrapEnvelope(get("/v1/me"));
}

// ---- dispatcher (ADR-014: ONE Content, route-resolved surface variant) -----

export function EmployeeListContent(): React.ReactElement {
  const { pathname } = useLocation();
  if (pathname.startsWith("/me/profile")) return <MyProfileView />;
  if (pathname.startsWith("/me/directory")) return <DirectoryView />;
  return <PeopleAdminView />;
}

// ============================================================================
// /people — yönetici personel listesi (personnel.jsx port)
// ============================================================================

// "Diğer" sekmesi: aktif ve izinde OLMAYAN her durum. Liste ucu
// `lifecycle_state`'i virgüllü bir "bunlardan biri" listesi olarak okuduğu için
// bileşik sekme de sunucuda daralır; eskiden yüklü pencerede istemcide
// süzülüyordu ve ilk sayfanın ötesindeki ayrılmış/işe alım öncesi kişileri
// göstermiyordu. Liste `LIFECYCLE_KEY`den türer: SDK'ya dokuzuncu bir durum
// eklendiğinde o harita derlemeyi kırar ve yeni durum "Diğer"e kendiliğinden girer.
const OTHER_LIFECYCLE_FILTER = (Object.keys(LIFECYCLE_KEY) as EmployeeLifecycleState[])
  .filter((state) => state !== "active" && state !== "on_leave")
  .join(",");

// Sekme → sunucu `lifecycle_state` süzgeci. `all` süzgeçsizdir.
const PEOPLE_TABS = [
  { value: "all", labelKey: "people.tab_all", serverFilter: undefined },
  { value: "active", labelKey: "people.tab_active", serverFilter: "active" },
  { value: "on_leave", labelKey: "people.tab_on_leave", serverFilter: "on_leave" },
  { value: "other", labelKey: "people.tab_other", serverFilter: OTHER_LIFECYCLE_FILTER },
] as const;

function PeopleAdminView(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const none = t("people.detail.none");
  // The lifecycle tab + department filter live in the ROUTER QUERY STRING
  // (?lifecycle_state=&department_id=), not component state, so a filtered
  // list is deep-linkable and survives refresh/back. The param names mirror
  // the backend GET /v1/employees filter params; the tab value is mapped to
  // the server's `lifecycle_state` list through PEOPLE_TABS ("other" is a
  // composite the server reads as a comma list). Unknown param values fall
  // back to the unfiltered "all" tab.
  // `searchParams` is read here only to carry the WHOLE filter set into the
  // detail/list hrefs below; each individual filter is owned by its hook.
  const [searchParams] = useSearchParams();
  const [statusTab, setStatusTab] = useTabParam(
    "lifecycle_state",
    PEOPLE_TABS.map((tabDef) => tabDef.value),
    "all",
  );
  const [departmentId, setDepartmentId] = useFilterParam("department_id");
  // The search box is in the URL too (SEM-6350): typed, then opened a person,
  // then Back used to return an EMPTY box over the full list.
  const [search, setSearch, committedSearch] = useDebouncedSearchParam("q");
  // The person opens as a PAGE at /people/:id (SEM-6354) — the name itself is
  // the link, so there is no second "Detay" column to hunt for.
  // The href carries `search` verbatim: the lifecycle tab and department filter
  // live in the query string, and the detail page's back link hands it back, so
  // returning to the list does not reset it to page 1 unfiltered (SEM-6350).
  const filterQuery = searchParams.toString();

  // Tab, department and search ALL narrow on the server. The search takes the
  // COMMITTED term (not the live input) so it rides the query key: a response
  // for an abandoned prefix lands in its own cache entry instead of replacing
  // the current one, and the debounce keeps a keystroke from being a request.
  const serverFilter = PEOPLE_TABS.find((tabDef) => tabDef.value === statusTab)?.serverFilter;
  const term = committedSearch.trim();
  const query = useKeysetQuery<EmployeeRow>({
    queryKey: ["employees", "list", statusTab, departmentId, term],
    fetchPage: (cursor) =>
      fetchEmployeesPage(cursor, {
        ...(serverFilter !== undefined && { lifecycleState: serverFilter }),
        ...(departmentId !== "" && { departmentId }),
        ...(term !== "" && { q: term }),
      }),
  });

  // The counters and the department options come from the server's own tally,
  // NOT from the loaded page. Counting a 100-row window printed "100+" and
  // "9+ Departman" for a tenant with exactly nine departments (E10); the
  // summary is exact and is scoped to the caller the same way the list is.
  const summary = useQuery({
    queryKey: ["employees", "summary"],
    queryFn: fetchEmployeeSummary,
  });
  const counts = summary.data;
  const total = counts?.total;
  // A state nobody is in is absent from the map, so a LOADED tally reads 0 there.
  const activeCount = counts === undefined ? undefined : (counts.by_lifecycle["active"] ?? 0);
  const onLeaveCount = counts === undefined ? undefined : (counts.by_lifecycle["on_leave"] ?? 0);
  const departmentCount = counts?.departments.length;
  // While the tally is in flight the tile shows the absent marker, never a 0
  // that reads as "nobody works here".
  const tally = (n: number | undefined): string => (n === undefined ? none : String(n));

  // A deep-linked department_id with nobody in it still renders as an option,
  // so the select always shows the active filter.
  const departmentNames = new Map<string, string>(
    (summary.data?.departments ?? []).map((d) => [d.department_id, d.department_name ?? none]),
  );
  if (departmentId !== "" && !departmentNames.has(departmentId)) {
    departmentNames.set(departmentId, none);
  }
  const departmentOptions = [...departmentNames.entries()].sort((a, b) =>
    a[1].localeCompare(b[1], i18n.language),
  );

  const filtering = statusTab !== "all" || departmentId !== "" || term !== "";

  const columns: readonly DataTableColumn<EmployeeRow>[] = [
    {
      id: "sicil",
      header: t("people.column.sicil"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap font-mono text-[12px] text-[var(--fg-muted)]">
          {row.employee_number ?? none}
        </span>
      ),
    },
    {
      id: "person",
      header: t("people.column.person"),
      cell: (row): React.ReactNode => (
        <div className="flex items-center gap-2.5">
          <Avatar size={32} alt={fullName(row)} initials={initialsOf(row)} />
          <div>
            <div className="whitespace-nowrap font-serif text-[14.5px] text-[var(--fg-primary)]">
              <PersonLink employeeId={row.employee_id} listQuery={filterQuery}>
                {fullName(row)}
              </PersonLink>
            </div>
            <div className="whitespace-nowrap text-[11.5px] text-[var(--fg-muted)]">
              {row.job_title ?? t("people.cell.no_title")}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "department",
      header: t("people.column.department"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-secondary)]">
          {row.department_name ?? none}
        </span>
      ),
    },
    {
      id: "email",
      header: t("people.column.email"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-secondary)]">
          {row.primary_work_email}
        </span>
      ),
    },
    {
      id: "start",
      header: t("people.column.start_date"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-muted)]">
          {row.start_date !== null
            ? formatDate(i18n.language, row.start_date)
            : t("people.cell.no_date")}
        </span>
      ),
    },
    {
      id: "lifecycle",
      header: t("people.column.lifecycle"),
      cell: (row): React.ReactNode => (
        <Badge tone={LIFECYCLE_TONE[row.lifecycle_state]}>
          {t(LIFECYCLE_KEY[row.lifecycle_state])}
        </Badge>
      ),
    },
  ];

  // The rows are exactly what the server returned for the active filters: no
  // client re-filter (that silently hid rows beyond the keyset window). Loading
  // and failure render HERE, inside the panel, and not in place of the tab bar:
  // a new search term is a new query key, so a page that swapped the whole
  // toolbar for a spinner would unmount the search box mid-typing and drop focus.
  const renderRows = (): React.ReactNode => {
    if (query.isLoading) return <Loading label={t("people.loading")} rows={5} />;
    if (query.isError) {
      return (
        <ErrorState title={t("people.error_title")} description={errorMessage(query.error, t)} />
      );
    }
    if (query.items.length === 0) {
      return filtering ? (
        <Empty
          title={t("people.empty_filtered_title")}
          description={t("people.empty_filtered_description")}
        />
      ) : (
        <Empty title={t("people.empty_title")} description={t("people.empty_description")} />
      );
    }
    return (
      <Card className="overflow-hidden">
        <DataTable<EmployeeRow>
          caption={t("people.table_caption")}
          columns={columns}
          rows={query.items}
          rowKey={(row): string => row.employee_id}
          virtualize
          hasNextPage={query.hasNextPage}
          onLoadMore={query.fetchNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          loadMoreLabel={t("people.load_more")}
        />
      </Card>
    );
  };

  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <PageHeader
        title={t("people.title")}
        description={t("people.lead", { total: tally(total), active: tally(activeCount) })}
        actions={
          <Button variant="primary" asChild>
            <RouterLink to="/people/create">
              <UserPlus size={14} aria-hidden="true" />
              {t("people.action_create")}
            </RouterLink>
          </Button>
        }
      />

      {/* KPI strip — mockup StatCard satırı (personnel.jsx). Exact server tally,
          independent of the active tab, so the breakdown stays meaningful on
          every filter. */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("people.kpi_total")}
          value={tally(total)}
          tone="accent"
          icon={<Users size={18} />}
        />
        <StatCard
          label={t("people.kpi_active")}
          value={tally(activeCount)}
          tone="success"
          icon={<CircleCheck size={18} />}
        />
        <StatCard
          label={t("people.kpi_on_leave")}
          value={tally(onLeaveCount)}
          tone="warning"
          icon={<CalendarDays size={18} />}
        />
        <StatCard
          label={t("people.kpi_departments")}
          value={tally(departmentCount)}
          tone="info"
          icon={<Building2 size={18} />}
        />
      </div>

      <Tabs
        value={statusTab}
        onValueChange={(value): void => {
          // The tab IS the ?lifecycle_state= query param ("all" = absent).
          setStatusTab(value);
        }}
        variant="segmented"
      >
        <div className="flex flex-wrap items-center gap-3">
          <TabList aria-label={t("people.tabs_label")}>
            {PEOPLE_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value}>
                {t(tab.labelKey)}
              </Tab>
            ))}
          </TabList>
          <div className="ml-auto flex flex-wrap items-center gap-2.5">
            <Select
              aria-label={t("people.department_filter_label")}
              value={departmentId}
              onChange={(e): void => {
                setDepartmentId(e.target.value);
              }}
            >
              <option value="">{t("people.department_all")}</option>
              {departmentOptions.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </Select>
            <div className="relative w-64">
              <Search
                size={14}
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-muted)]"
              />
              <Input
                aria-label={t("people.search_label")}
                placeholder={t("people.search_placeholder")}
                value={search}
                onChange={(e): void => {
                  setSearch(e.target.value);
                }}
                className="nai-input-search"
              />
            </div>
          </div>
        </div>
        {PEOPLE_TABS.map((tab) => (
          <TabPanel key={tab.value} value={tab.value}>
            {renderRows()}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}

// ============================================================================
// /me/directory — çalışan rehberi (emp-directory.jsx port)
// ============================================================================
//
// Reads GET /v1/employees/directory, NOT the personnel list the other views in
// this file use. That list is scope-tiered, so for a plain employee it is one
// row (their own) and this "directory" showed exactly one person (E27). The
// directory route returns a fixed projection — name, work email, title,
// department — for every active coworker in the caller's company grant. There is
// no sicil, lifecycle or start date on this screen because they are not in the
// response, not because they are hidden.

const DIRECTORY_VIEWS = ["grid", "list"] as const;

function DirectoryView(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const none = t("people.detail.none");
  const [search, setSearch, committedSearch] = useDebouncedSearchParam("q");
  // The department is a URL filter too: a department name elsewhere links here
  // (`?department_id=`), and the view choice below is shareable for the same reason.
  const [departmentId, setDepartmentId] = useFilterParam("department_id");
  const [view, setView] = useTabParam("view", DIRECTORY_VIEWS, "grid");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // A whitespace-only term is not a search, and the route rejects it as an empty `q`.
  const term = committedSearch.trim();

  // The unfiltered window feeds the KPI strip and the department options. With
  // no filter active it IS the list (one cache entry, one fetch).
  const everyone = useKeysetQuery<DirectoryEntry>({
    queryKey: [...DIRECTORY_KEY, "list", "", ""],
    fetchPage: (cursor) => fetchDirectoryPage(cursor === null ? {} : { cursor }),
  });
  // Search and department narrow SERVER-side: filtering the loaded window would
  // miss everyone past its first page, which is the tenant size this screen is for.
  const usesServerFilter = term !== "" || departmentId !== "";
  const narrowed = useKeysetQuery<DirectoryEntry>({
    queryKey: [...DIRECTORY_KEY, "list", term, departmentId],
    fetchPage: (cursor) =>
      fetchDirectoryPage({ ...(cursor !== null && { cursor }), q: term, departmentId }),
    enabled: usesServerFilter,
  });
  const list = usesServerFilter ? narrowed : everyone;
  const rows = list.items;

  // Resolve the caller's own employee id so their card carries the "Ben" pill
  // (mockup self marker). Shares the cache key with the profile surface.
  const me = useQuery({ queryKey: ["me", "identity"], queryFn: fetchMe });
  const myEmployeeId = me.data?.employee_id ?? null;

  const selected = rows.find((r) => r.employee_id === selectedId) ?? rows[0] ?? null;

  const departmentCount = new Set(
    everyone.items.map((r) => r.department_id).filter((d): d is string => d !== null),
  ).size;
  // Names come from both queries: a deep-linked department outside the loaded
  // window still gets its name from the narrowed page that just answered.
  const departmentNames = new Map<string, string>();
  for (const entry of [...everyone.items, ...rows]) {
    if (entry.department_id !== null) {
      departmentNames.set(entry.department_id, entry.department_name ?? none);
    }
  }
  if (departmentId !== "" && !departmentNames.has(departmentId)) {
    departmentNames.set(departmentId, none);
  }
  const departmentOptions = [...departmentNames.entries()].sort((a, b) =>
    a[1].localeCompare(b[1], i18n.language),
  );

  const personCard = (row: DirectoryEntry): React.ReactNode => {
    const isSel = selected !== null && selected.employee_id === row.employee_id;
    const isMe = myEmployeeId !== null && myEmployeeId === row.employee_id;
    return (
      <PersonCard
        key={row.employee_id}
        selected={isSel}
        onClick={(): void => {
          setSelectedId(row.employee_id);
        }}
        avatar={<Avatar size={40} alt={fullName(row)} initials={initialsOf(row)} />}
        name={fullName(row)}
        nameBadge={isMe ? <Badge tone="info">{t("directory.me_badge")}</Badge> : undefined}
        subtitle={row.primary_work_email}
        footerStart={
          <span className="min-w-0 truncate text-[12px] text-[var(--fg-secondary)]">
            {row.job_title ?? none}
          </span>
        }
        footerEnd={
          row.department_name !== null ? (
            <Badge tone="info">{row.department_name}</Badge>
          ) : undefined
        }
      />
    );
  };

  // The toolbar stays mounted while a narrowed query loads. Swapping the whole
  // screen for a spinner on every committed keystroke would unmount the search
  // box and drop the caret mid-word.
  const results = list.isLoading ? (
    <Loading label={t("people.loading")} rows={5} />
  ) : list.isError ? (
    <ErrorState title={t("people.error_title")} description={errorMessage(list.error, t)} />
  ) : rows.length === 0 ? (
    <Empty title={t("directory.empty_title")} description={t("directory.empty_description")} />
  ) : view === "grid" ? (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {rows.map(personCard)}
    </div>
  ) : (
    <Card className="overflow-hidden">
      {rows.map((row, i) => {
        const isSel = selected !== null && selected.employee_id === row.employee_id;
        const isMe = myEmployeeId !== null && myEmployeeId === row.employee_id;
        return (
          <button
            key={row.employee_id}
            type="button"
            aria-pressed={isSel}
            onClick={(): void => {
              setSelectedId(row.employee_id);
            }}
            className={`grid w-full grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 text-left ${
              i > 0 ? "border-t border-[var(--border-subtle)]" : ""
            } ${isSel ? "bg-[var(--accent-subtle-bg)]" : ""}`}
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar size={32} alt={fullName(row)} initials={initialsOf(row)} />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-serif text-[14.5px] text-[var(--fg-primary)]">
                    {fullName(row)}
                  </span>
                  {isMe && <Badge tone="info">{t("directory.me_badge")}</Badge>}
                </div>
                <div className="truncate text-[11.5px] text-[var(--fg-muted)]">
                  {row.job_title ?? row.primary_work_email}
                </div>
              </div>
            </div>
            {row.department_name !== null && <Badge tone="info">{row.department_name}</Badge>}
          </button>
        );
      })}
    </Card>
  );

  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <PageHeader
        title={t("directory.title")}
        description={t("directory.lead", {
          total: formatCountWithMore(everyone.items.length, everyone.hasNextPage),
        })}
        actions={
          <Button variant="secondary" asChild>
            <RouterLink to="/me/org">
              <Network size={14} aria-hidden="true" />
              {t("directory.org_chart")}
            </RouterLink>
          </Button>
        }
      />

      {/* Rehberin neyi gösterdiğini çalışana söyler: meslektaşları da sizin
          adınızı, ünvanınızı, departmanınızı ve iş e-postanızı görür. */}
      <Alert severity="info">{t("directory.privacy_note")}</Alert>

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          {/* Mini metrikler */}
          {!everyone.isLoading && !everyone.isError && (
            <div className="grid grid-cols-2 gap-3.5">
              <StatCard
                label={t("directory.kpi_total")}
                value={formatCountWithMore(everyone.items.length, everyone.hasNextPage)}
                tone="accent"
                icon={<Users size={18} />}
              />
              <StatCard
                label={t("directory.kpi_departments")}
                value={formatCountWithMore(departmentCount, everyone.hasNextPage)}
                tone="info"
                icon={<Building2 size={18} />}
              />
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative w-72">
              <Search
                size={14}
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-muted)]"
              />
              <Input
                aria-label={t("directory.search_label")}
                placeholder={t("directory.search_placeholder")}
                value={search}
                onChange={(e): void => {
                  setSearch(e.target.value);
                }}
                className="nai-input-search"
              />
            </div>
            <Select
              aria-label={t("people.department_filter_label")}
              value={departmentId}
              onChange={(e): void => {
                setDepartmentId(e.target.value);
              }}
            >
              <option value="">{t("people.department_all")}</option>
              {departmentOptions.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </Select>
            <ViewToggle
              aria-label={t("directory.view_label")}
              value={view}
              onChange={setView}
              options={[
                {
                  value: "grid",
                  icon: <LayoutGrid size={14} aria-hidden="true" />,
                  label: t("directory.view_grid"),
                },
                {
                  value: "list",
                  icon: <Rows3 size={14} aria-hidden="true" />,
                  label: t("directory.view_list"),
                },
              ]}
            />
            <span className="ml-auto text-[12px] text-[var(--fg-muted)]">
              {t("directory.match_count", { count: rows.length })}
            </span>
          </div>

          {results}

          {list.hasNextPage && (
            <Button
              variant="secondary"
              onClick={list.fetchNextPage}
              disabled={list.isFetchingNextPage}
            >
              {t("people.load_more")}
            </Button>
          )}
        </div>

        {/* Sticky kişi paneli */}
        {!list.isLoading && !list.isError && (
          <div className="sticky top-6">
            {selected === null ? (
              <Card>
                <CardBody>
                  <p className="text-[13px] text-[var(--fg-muted)]">
                    {t("directory.empty_description")}
                  </p>
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardBody>
                  <div className="mb-4 flex flex-col items-center border-b border-[var(--border-subtle)] pb-4 text-center">
                    <Avatar size={64} alt={fullName(selected)} initials={initialsOf(selected)} />
                    <div className="mt-3 font-serif text-[18px] font-medium text-[var(--fg-primary)]">
                      {fullName(selected)}
                    </div>
                    <div className="mt-1 text-[12.5px] text-[var(--fg-secondary)]">
                      {selected.job_title ?? none}
                    </div>
                    {myEmployeeId !== null && myEmployeeId === selected.employee_id && (
                      <span className="mt-2">
                        <Badge tone="info">{t("directory.panel_me")}</Badge>
                      </span>
                    )}
                  </div>
                  <FieldRow
                    label={t("directory.panel_email")}
                    value={selected.primary_work_email}
                  />
                  <FieldRow
                    label={t("directory.panel_department")}
                    value={selected.department_name ?? none}
                  />
                  <div className="mt-4 flex flex-col gap-2">
                    <Button variant="primary" asChild>
                      <a href={`mailto:${selected.primary_work_email}`}>
                        <Mail size={14} aria-hidden="true" />
                        {t("directory.panel_send_email")}
                      </a>
                    </Button>
                    <Button variant="secondary" asChild>
                      <RouterLink to="/me/org">
                        <Network size={14} aria-hidden="true" />
                        {t("directory.panel_view_org")}
                      </RouterLink>
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Çizimdeki "İzin bakiyem" kartı (emp-profile.jsx PRLeaveBar bloğu), gerçek
// veriyle: GET /v1/leave/balances?mine=true. `mine=true` sunucudaki yetki
// sınırıdır — yönetici yetkisi de taşıyan bir çağıranın yanıtını istemcide
// süzmek o sınırı ekrana taşımak olurdu (leave-balance-content.tsx aynı
// gerekçeyle böyle çağırıyor).
//
// Tek sayfa okunur ve o sayfanın ilk dördü çizilir: bu bir ÖZET kartıdır,
// profilin dar sağ kolonunda yaşar ve tamamı /me/leave/balance ekranındadır.
const BALANCE_SUMMARY_LIMIT = 4;

function MyLeaveBalanceCard(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    // Bakiye ekranının anahtarı ["leave","balances","mine"] bir KEYSET sayfa
    // listesidir; bu tek sayfalık özet onunla aynı kovaya girmemeli.
    queryKey: ["leave", "balances", "mine", "profile-summary"],
    queryFn: () => fetchLeaveBalancesPage(null, true),
  });
  const rows = (query.data?.items ?? []).slice(0, BALANCE_SUMMARY_LIMIT);

  return (
    <Card>
      <CardHeader>
        <div>
          <div className="nai-card-title">{t("profile.balance_section")}</div>
        </div>
        <RouterLink
          to="/me/leave/balance"
          className="text-[12.5px] text-[var(--accent)]"
        >
          {t("profile.balance_see_all")}
        </RouterLink>
      </CardHeader>
      <CardBody>
        {query.isLoading ? (
          <Loading label={t("leave.balance.loading")} rows={2} />
        ) : query.isError ? (
          // Kart yok olmaz: boş bakiye ile çalışmayan uç aynı şey değildir ve
          // sessiz kaybolma ikisini tek görüntüye indiriyordu.
          <ErrorState
            title={t("leave.balance.error_title")}
            description={errorMessage(query.error, t)}
          />
        ) : rows.length === 0 ? (
          <p className="text-[12.5px] text-[var(--fg-muted)]">
            {t("profile.balance_empty")}
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((balance) => {
              // Gün olarak okunur (E12); saat, bakiye ekranında ikinci satırdır.
              const amount =
                formatLeaveSpan(balance.balance_hours, i18n.language, t) ?? t("common.unknown");
              const cap =
                balance.cap_hours === null
                  ? null
                  : formatLeaveSpan(balance.cap_hours, i18n.language, t);
              const caption =
                cap === null
                  ? t("profile.balance_hours", { amount })
                  : t("profile.balance_hours_of_cap", { amount, cap });
              return (
                <div key={balance.balance_id}>
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="text-[12.5px] font-medium text-[var(--fg-primary)]">
                      {/* Em dash iki farklı poliçeyi tek satıra indirirdi;
                          çözülmemiş ad bakiye ekranındaki gibi kısa kimlik. */}
                      {balance.policy_name ?? balance.policy_id.slice(0, 8)}
                    </span>
                    <span className="text-[11.5px] text-[var(--fg-muted)]">{caption}</span>
                  </div>
                  {balance.cap_hours !== null && Number(balance.cap_hours) > 0 && (
                    <MeterBar
                      ratio={Number(balance.balance_hours) / Number(balance.cap_hours)}
                      label={caption}
                    />
                  )}
                  {/* Askıya alınmış/kapatılmış bakiye hak edilmiş bakiye gibi
                      okunmamalı; aktif olan durumunu tekrarlamaz. */}
                  {balance.status !== "active" && (
                    <div className="mt-1">
                      <Badge size="sm" tone="neutral">
                        {t(`leave.balance.status.${balance.status}`)}
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ============================================================================
// /me/profile — kendi profilim (emp-profile.jsx port, çekirdek alanlar)
// ============================================================================

function MyProfileView(): React.ReactElement {
  const { t, i18n } = useTranslation();
  // Dil tercihi bu yüzeyde yaşar (TopBar'dan taşındı, 2026-07-30): setLocale
  // hem yerel dili değiştirir hem preferred_locale'i sunucuya yazar — bildirim
  // dili o kolondan çözüldüğü için ikisi TEK eylemde birleşmek zorunda.
  const { locale, setLocale, syncError } = useLocalePreference();
  // Saat dilimi de burada: kiracı varsayılanını yönetici ekranı yazar, bu ise
  // kişinin kendi geçersiz kılması (SEM-6939). İkisi aynı kartta çünkü ikisi de
  // "bu hesap bana nasıl görünsün" sorusunun cevabı.
  const timeZone = useTimeZonePreference();
  const { capabilities } = useEffectivePermissions();
  const canRequestDocuments = capabilities.has("document_requests.create");

  const me = useQuery({ queryKey: ["me", "identity"], queryFn: fetchMe });
  const myEmployeeId = me.data?.employee_id ?? null;
  const emp = useQuery({
    queryKey: ["employees", "detail", myEmployeeId],
    queryFn: () => fetchEmployee(myEmployeeId ?? ""),
    enabled: myEmployeeId !== null,
  });

  const none = t("profile.none");

  if (me.isLoading || (myEmployeeId !== null && emp.isLoading)) {
    return (
      <div className="mx-auto max-w-[1100px]">
        <Loading label={t("profile.loading")} rows={5} />
      </div>
    );
  }
  if (me.isError || emp.isError) {
    return (
      <div className="mx-auto max-w-[1100px]">
        <ErrorState
          title={t("profile.error_title")}
          description={errorMessage(me.isError ? me.error : emp.error, t)}
        />
      </div>
    );
  }

  const row = emp.data;
  if (myEmployeeId === null || row === undefined) {
    return (
      <div className="mx-auto max-w-[1100px] space-y-6">
        <PageHeader title={t("profile.title")} description={t("profile.lead")} />
        <Empty
          title={t("profile.unbound_title")}
          description={t("profile.unbound_description")}
        />
      </div>
    );
  }

  // Sağ kolon hızlı erişim — mockup'taki bakiye/bordro/zimmet kartlarının
  // backend'li karşılıkları MEVCUT route'lara bağlanır (görsel veri klonu yok).
  const quickLinks = [
    { to: "/me/leave/balance", labelKey: "profile.quick_leave", icon: <CalendarCheck2 size={14} aria-hidden="true" /> },
    { to: "/me/payroll", labelKey: "profile.quick_payroll", icon: <ReceiptText size={14} aria-hidden="true" /> },
    { to: "/me/assets", labelKey: "profile.quick_assets", icon: <Package size={14} aria-hidden="true" /> },
    // The two letters people ask HR for most; the request form opens with the
    // kind already chosen, and the server still decides whether it may be filed.
    ...(canRequestDocuments
      ? [
          { to: "/me/document-requests?kind=employment_confirmation", labelKey: "profile.quick_employment_letter", icon: <FileText size={14} aria-hidden="true" /> },
          { to: "/me/document-requests?kind=salary_letter", labelKey: "profile.quick_salary_letter", icon: <FileText size={14} aria-hidden="true" /> },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <PageHeader title={t("profile.title")} description={t("profile.lead")} />

      {/* Hero kartı (PRHero portu) */}
      <Card data-tour="profile-identity">
        <CardBody>
          <div className="flex flex-wrap items-start gap-5">
            <Avatar size={64} alt={fullName(row)} initials={initialsOf(row)} />
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge tone={LIFECYCLE_TONE[row.lifecycle_state]}>
                  {t(LIFECYCLE_KEY[row.lifecycle_state])}
                </Badge>
                <span className="font-mono text-[12px] text-[var(--fg-muted)]">
                  {t("profile.sicil", { sicil: row.employee_number ?? none })}
                </span>
                {row.department_name !== null && (
                  <Badge tone="info">{row.department_name}</Badge>
                )}
              </div>
              <div className="font-serif text-[26px] tracking-[-0.01em] text-[var(--fg-primary)]">
                {fullName(row)}
              </div>
              <div className="mt-0.5 font-serif text-[14.5px] text-[var(--fg-secondary)]">
                {row.job_title ?? none}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-[var(--fg-secondary)]">
                <span className="flex items-center gap-1.5">
                  <Mail size={14} aria-hidden="true" className="text-[var(--fg-muted)]" />
                  {row.primary_work_email}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-4 py-3">
              <div className="min-w-[80px] text-center">
                <div className="text-[11px] text-[var(--fg-muted)]">
                  {t("profile.hero_start")}
                </div>
                <div className="mt-0.5 font-serif text-[15px] font-medium">
                  {row.start_date !== null ? formatDate(i18n.language, row.start_date) : none}
                </div>
                {row.start_date !== null && (
                  <div className="mt-0.5 text-[11px] text-[var(--fg-muted)]">
                    {formatTenureDuration(i18n.language, t, row.start_date)}
                  </div>
                )}
              </div>
              <div className="min-w-[80px] text-center">
                <div className="text-[11px] text-[var(--fg-muted)]">
                  {t("profile.hero_type")}
                </div>
                <div className="mt-0.5 font-serif text-[15px] font-medium">
                  {row.employment_type !== null
                    ? t(`people.employment_type.${row.employment_type}`)
                    : none}
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid items-start gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* SOL — özlük bilgileri */}
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <div>
                <div className="nai-card-title">{t("profile.job_section")}</div>
              </div>
            </CardHeader>
            <CardBody>
              <FieldRow label={t("profile.field_position")} value={row.job_title ?? none} />
              <FieldRow
                label={t("profile.field_department")}
                value={
                  row.department_name === null ? (
                    none
                  ) : (
                    <DepartmentLink departmentId={row.department_id} surface="directory">
                      {row.department_name}
                    </DepartmentLink>
                  )
                }
              />
              <FieldRow
                label={t("profile.field_manager")}
                value={
                  // Linked only when the NAME resolved — the admin drawer rule:
                  // a bare id under the link would offer to open the absent marker.
                  row.manager_name === null ? (
                    none
                  ) : (
                    <PersonLink employeeId={row.manager_id}>{row.manager_name}</PersonLink>
                  )
                }
              />
              <FieldRow
                label={t("profile.field_employment_type")}
                value={
                  row.employment_type !== null
                    ? t(`people.employment_type.${row.employment_type}`)
                    : none
                }
              />
              <FieldRow
                label={t("profile.field_start_date")}
                value={row.start_date !== null ? formatDate(i18n.language, row.start_date) : none}
              />
              <FieldRow
                label={t("profile.field_lifecycle")}
                value={t(LIFECYCLE_KEY[row.lifecycle_state])}
              />
              <FieldRow
                label={t("profile.field_sicil")}
                value={row.employee_number ?? none}
                mono
              />
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <div>
                <div className="nai-card-title">{t("profile.contact_section")}</div>
              </div>
            </CardHeader>
            <CardBody>
              <FieldRow label={t("profile.field_email")} value={row.primary_work_email} />
            </CardBody>
          </Card>
          <PersonalInfoCard source={{ kind: "self" }} />
        </div>

        {/* SAĞ — hızlı erişim + tercihler */}
        <div className="space-y-5">
          <MyLeaveBalanceCard />
          <Card data-tour="profile-quick-links">
            <CardHeader>
              <div>
                <div className="nai-card-title">{t("profile.quick_title")}</div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="flex flex-col gap-2">
                {quickLinks.map((link) => (
                  <Button key={link.to} variant="secondary" asChild>
                    <RouterLink to={link.to} className="justify-start">
                      {link.icon}
                      {t(link.labelKey)}
                    </RouterLink>
                  </Button>
                ))}
              </div>
            </CardBody>
          </Card>
          <Card data-tour="profile-preferences">
            <CardHeader>
              <div>
                <div className="nai-card-title">{t("profile.preferences_section")}</div>
              </div>
            </CardHeader>
            <CardBody>
              <FieldRow
                label={t("profile.field_language")}
                value={
                  <LanguageSwitcher
                    value={locale}
                    onChange={setLocale}
                    aria-label={t("app.language_label")}
                    getOptionLabel={(language) => t("app.change_language_to", { language })}
                  />
                }
              />
              {/* Sunucu yazımı başarısız olsa da yerel tercih uygulanmış
                  kalır; hata sessizce yutulmaz — bildirim dili eski kalacaksa
                  kullanıcı bunu görmeli. */}
              {syncError !== null && (
                <p role="alert" className="mt-2 text-sm text-[var(--color-error)]">
                  {errorMessage(syncError, t)}
                </p>
              )}
              <FieldRow
                label={t("profile.field_time_zone")}
                value={
                  <Select
                    size="sm"
                    aria-label={t("profile.field_time_zone")}
                    disabled={timeZone.isLoading || timeZone.isSaving}
                    value={timeZone.own ?? ""}
                    onChange={(event) => {
                      // Boş dize DB'de yasaklı (göç 0226 CHECK'i) ve zaten
                      // "seçim yok"un taşıyıcısı değil — null'a çevrilir.
                      const next = event.target.value;
                      timeZone.setTimeZone(next === "" ? null : next);
                    }}
                  >
                    <option value="">
                      {/* Ham kimlik, farksız: bu seçenek "hangi saat" değil
                          "kiracının seçtiği dilim" diyor ve etiketi zaten
                          parantez içinde. */}
                      {timeZone.effective === null
                        ? t("profile.time_zone_default_loading")
                        : t("profile.time_zone_default", { zone: timeZone.effective })}
                    </option>
                    {supportedTimeZones().map((zone) => (
                      <option key={zone} value={zone}>
                        {timeZoneLabel(zone, i18n.language)}
                      </option>
                    ))}
                  </Select>
                }
              />
              {timeZone.saveError !== null && (
                <p role="alert" className="mt-2 text-sm text-[var(--color-error)]">
                  {errorMessage(timeZone.saveError, t)}
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
